import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  FileText,
  Mail,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useFeatureFlags } from "@/feature-flags/FeatureFlagsContext";
import FeatureDisabled from "@/feature-flags/FeatureDisabled";
import AppNav from "@/components/app/AppNav";
import AIDisclosureBadge from "@/components/AIDisclosureBadge";
import { Badge } from "@/design-system";

const LABEL = {
  experian: "Experian",
  equifax: "Equifax",
  transunion: "TransUnion",
  furnisher: "Furnisher",
  collection_agency: "Collection agency",
};

const TYPE_TONE = {
  experian:          "accent",
  equifax:           "accent",
  transunion:        "accent",
  furnisher:         "warning",
  collection_agency: "warning",
};

export default function DisputeLetters() {
  const { isEnabled } = useFeatureFlags();
  const [letters, setLetters] = useState(null);
  const [error, setError] = useState("");
  const [disclaimer, setDisclaimer] = useState("");

  const load = useCallback(() => {
    api
      .get("/dispute-letters")
      .then((r) => setLetters(r.data || []))
      .catch((err) => setError(formatApiError(err)));
  }, []);

  useEffect(() => {
    load();
    api.get("/site-settings").then((r) => setDisclaimer(r.data?.disclaimer_text || "")).catch(() => {});
  }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this letter? This cannot be undone.")) return;
    try {
      await api.delete(`/dispute-letters/${id}`);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const downloadUrl = (id, fmt) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/dispute-letters/${id}/download?format=${fmt}`;
  };

  return (
    <main className="min-h-screen text-[var(--ds-color-ink)] pb-20 bg-[var(--ds-color-background)]">
      <AppNav />
      {!isEnabled("dispute_letter_generator") ? (
        <FeatureDisabled
          testId="dispute-letters-feature-disabled"
          title="Dispute letter generator is temporarily disabled"
          message="An administrator has paused this feature. Previously generated letters remain accessible until re-enabled."
        />
      ) : (
      <div className="mx-auto max-w-5xl px-4 md:px-8 pt-10">
        <header>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)] flex items-center gap-3 flex-wrap">
            <span>Credit Repair · Letters</span>
            <AIDisclosureBadge />
          </div>
          <h1
            className="mt-2 text-[var(--ds-color-ink)] leading-tight"
            style={{
              fontFamily: "var(--ds-font-display)",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontSize: "clamp(2rem, 3.4vw, 2.75rem)",
            }}
          >
            Your dispute letters.
          </h1>
          <p
            data-testid="dispute-letters-page-subtitle"
            className="mt-3 text-[14px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-2xl"
          >
            Draft, review, and manage FCRA-based credit dispute letters.
          </p>
        </header>

        {disclaimer && (
          <div
            data-testid="dispute-letters-disclaimer-banner"
            className="mt-5 rounded-[var(--ds-radius-md)] p-4 text-[13px] text-[var(--ds-color-ink-muted)] flex items-start gap-2"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border)",
            }}
          >
            <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--ds-color-accent)" }} aria-hidden />
            <span>{disclaimer}</span>
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-[var(--ds-radius-md)] p-4 text-[13px]" role="alert" style={{
            background: "var(--ds-color-danger-soft)",
            border: "1px solid var(--ds-color-danger)",
            color: "var(--ds-color-danger)",
          }}>
            {error}
          </div>
        )}

        {letters === null && !error && (
          <p className="mt-10 text-[var(--ds-color-ink-muted)]">Loading…</p>
        )}

        {letters !== null && letters.length === 0 && (
          <div
            className="mt-8 rounded-[var(--ds-radius-lg)] p-10 text-center"
            style={{
              background: "var(--ds-color-surface)",
              border: "1px solid var(--ds-color-border)",
              boxShadow: "var(--ds-shadow-card)",
            }}
          >
            <span
              className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--ds-radius-md)] mx-auto"
              style={{ background: "var(--ds-color-accent-soft)", color: "var(--ds-color-accent)" }}
            >
              <FileText className="w-5 h-5" aria-hidden />
            </span>
            <h2
              className="mt-4 text-[var(--ds-color-ink)]"
              style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.25rem", letterSpacing: "-0.02em" }}
            >
              No letters yet.
            </h2>
            <p className="mt-2 text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)] max-w-md mx-auto">
              Start by selecting items in your Credit Improvement center, then click{" "}
              <strong>Generate Dispute Letters</strong>.
            </p>
            <Link
              to="/credit-improvement"
              data-testid="dispute-letters-empty-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-[var(--ds-radius-md)] text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
              style={{
                background: "var(--ds-color-accent)",
                color: "var(--ds-color-accent-ink)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
            >
              Go to Credit Improvement
            </Link>
          </div>
        )}

        {letters !== null && letters.length > 0 && (
          <ul className="mt-8 space-y-4" data-testid="dispute-letters-list">
            {letters.map((l) => {
              const typeTone = TYPE_TONE[l.letter_type] || "accent";
              return (
                <li
                  key={l.id}
                  data-testid={`dispute-letters-row-${l.id}`}
                  className="rounded-[var(--ds-radius-lg)] p-5 md:p-6"
                  style={{
                    background: "var(--ds-color-surface)",
                    border: "1px solid var(--ds-color-border)",
                    boxShadow: "var(--ds-shadow-card)",
                  }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <Badge tone={typeTone} size="sm">
                        {LABEL[l.letter_type] || l.letter_type}
                      </Badge>
                      <h3
                        className="mt-2 text-[var(--ds-color-ink)]"
                        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.35 }}
                      >
                        {l.recipient_name}
                      </h3>
                      <p className="mt-1 text-[12px] text-[var(--ds-color-ink-muted)]">
                        {l.item_count} item{l.item_count === 1 ? "" : "s"} · generated{" "}
                        {new Date(l.generated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={`/dispute-letters/${l.id}`}
                        data-testid={`dispute-letters-preview-${l.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[var(--ds-radius-md)] text-[12px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
                        style={{
                          background: "var(--ds-color-surface-muted)",
                          border: "1px solid var(--ds-color-border-strong)",
                          color: "var(--ds-color-ink)",
                          transitionDuration: "var(--ds-duration-fast)",
                        }}
                      >
                        Preview / edit
                      </Link>
                      <DownloadGroup id={l.id} downloadUrl={downloadUrl} />
                      <button
                        type="button"
                        data-testid={`dispute-letters-delete-${l.id}`}
                        onClick={() => handleDelete(l.id)}
                        title="Delete"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-[var(--ds-radius-pill)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
                        style={{
                          border: "1px solid var(--ds-color-danger)",
                          color: "var(--ds-color-danger)",
                          transitionDuration: "var(--ds-duration-fast)",
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}
    </main>
  );
}

function DownloadGroup({ id, downloadUrl }) {
  const formats = ["pdf", "docx", "txt"];
  return (
    <div className="inline-flex items-center gap-1">
      {formats.map((fmt) => (
        <a
          key={fmt}
          href={downloadUrl(id, fmt)}
          data-testid={`dispute-letters-download-${id}-${fmt}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[var(--ds-radius-md)] text-[10px] uppercase tracking-[0.16em] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] focus-visible:ring-offset-2"
          style={{
            background: "var(--ds-color-surface-muted)",
            border: "1px solid var(--ds-color-border-strong)",
            color: "var(--ds-color-ink)",
            transitionDuration: "var(--ds-duration-fast)",
          }}
        >
          <Download className="w-3 h-3" aria-hidden />
          {fmt}
        </a>
      ))}
    </div>
  );
}
